import { capitalize } from '@mui/material';
import {
  addDays,
  differenceInDays,
  endOfDay,
  format,
  isBefore,
  isSameDay,
} from 'date-fns';
import { EmployeesToBusinesses, Roles, Trips } from '@prisma/client';
import { formatInTimeZone } from 'date-fns-tz';
import {
  EmployeeAvailabilityErrorParams,
  EmployeeAvailabilityError,
  EmployeeAvailablityErrorResponse,
  ShortDay,
  ResourceBlock,
  EmployeesRole,
  EmployeesResourceMap,
} from '../../types/employees';
import { OnSchedCommonType } from '../../types/on-sched';
import {
  convertIndexToShortDay,
  convertMinsToHrsMins,
  convertMinutesToTimeDiff,
  convertOnschedHoursToDate,
  convertOnschedHoursToDateFromOffset,
  convertOnschedHoursToDateFromTimezone,
  convertShortDayToFull,
  getArtistName,
  isUserAdminOrManager,
  isUserEmployeeOrAdmin,
} from '../../utils/global/helper';
import {
  EmployeeToBusiness,
  FilterOption,
  LoadedUser,
  PayoutDistributionOptions,
} from '../../interfaces';
import { DAYS_ORDER, MANAGER_ROLES } from '../../constants/employees';
import { Offboarding } from '../../types/offboardings';
import { BusinessService } from '../../types/businesses';
import {
  getArtistOptions,
  generateColorForArtist,
} from '../../services/business';
import { CreateArtistValues } from '../../utils/form/artistForm';
import { getEmployeeTimezoneOffset } from '../calendar';
import {
  UNASSIGNED_RESOURCE_COLOR,
  UNASSIGNED_RESOURCE_ID,
} from '../../constants/calendar';

export const getEmployeeAvailabilityError = (
  params: EmployeeAvailabilityErrorParams,
): EmployeeAvailablityErrorResponse | null => {
  const { currentUser, employeeId, employees } = params;
  const isSelf = currentUser.userId === employeeId;
  const employee = employees.find(e => e.employeeId === employeeId);
  const name = employee?.user?.firstName || 'this employee';
  switch (params.code) {
    case EmployeeAvailabilityError.InvalidDuration: {
      const updateString = isSelf
        ? 'update your weekly availability'
        : `update ${name}'s weekly availability`;
      return {
        errorTitle: 'Invalid Appt. Length',
        errorMessage: `To schedule appointments ${convertMinsToHrsMins(
          params.durationMins,
        )} or longer, ${updateString}. ${
          isSelf ? 'Your' : `${capitalize(name)}'s`
        } longest availability is ${convertMinsToHrsMins(
          params.maximumDurationMins,
        )}.`,
        type: 'error',
        code: params.code,
        maximumDuration: params.maximumDurationMins,
      };
    }
    case EmployeeAvailabilityError.FewAvailableDays: {
      const fullAvailableDays = params.days.map(day =>
        convertShortDayToFull(day, true),
      );
      const daysString = fullAvailableDays.reduce<string>((acc, day, index) => {
        if (index === 0) return day;
        if (index === fullAvailableDays.length - 1) {
          const separator = fullAvailableDays.length === 2 ? ' and ' : ', and ';
          return `${acc}${separator}${day}`;
        }
        return `${acc}, ${day}`;
      }, '');
      const selfString = isSelf ? "you're" : `${name} is`;
      return {
        errorTitle: 'Few Available Days',
        errorMessage: `Are you sure? Since ${selfString} not open for ${convertMinsToHrsMins(
          params.durationMins,
        )} some days, this customer will only be able to schedule on ${daysString}`,
        type: 'warning',
        code: params.code,
      };
    }
    default:
      return null;
  }
};

export const getAvailableDaysFromDuration = (
  availability: OnSchedCommonType.Availability,
  durationMins: number,
) => {
  const entries = Object.entries(availability) as [
    keyof OnSchedCommonType.Availability,
    OnSchedCommonType.Time,
  ][];
  const timeDiff = convertMinutesToTimeDiff(durationMins);
  return entries.reduce(
    (currentList, [day, data]) => {
      const dayDuration = data.endTime - data.startTime;
      if (dayDuration >= timeDiff) {
        return [...currentList, day];
      }
      return currentList;
    },
    [] as (keyof OnSchedCommonType.Availability)[],
  );
};

export const getAvailabilityWarning = (
  localAvailability: Partial<OnSchedCommonType.Availability>,
) => {
  const daysWithEndBeforeStart = Object.entries(localAvailability).reduce(
    (acc, [key, value]) => {
      if (value.endTime < value.startTime) {
        acc.push([key, value]);
      }
      return acc;
    },
    [] as [string, OnSchedCommonType.Time][],
  );

  if (daysWithEndBeforeStart.length > 0) {
    const days = daysWithEndBeforeStart
      .map(
        ([shortDay, values]) =>
          `${convertShortDayToFull(shortDay as ShortDay)} (${format(
            convertOnschedHoursToDate(values.endTime),
            'h:mm a',
          )})`,
      )
      .join(', ');

    const message =
      daysWithEndBeforeStart.length > 1
        ? `The end times for the following days are before the start times:\n\n${days}\n\n`
        : `The end time for ${days} is before the start time (${format(
            convertOnschedHoursToDate(daysWithEndBeforeStart[0][1].startTime),
            'h:mm a',
          )}). `;

    const sub = `${message}Are you sure you want to continue?`;
    return {
      title: 'Confirm Hours',
      message: sub,
    };
  }

  return null;
};

export function getLiabilityWaiverText(
  firstName: string,
  lastName: string,
  currentUser: LoadedUser,
) {
  return `I (referred to as "I" or "me") desire to receive a tattoo (the "Activity") from ${firstName} ${lastName}, a Tattoo Artist based out of ${currentUser.business.city}, ${currentUser.business.state} (the "Company"). I agree, on behalf of myself, my heirs, and my personal representatives, to all the terms and conditions set forth in this Consent and Release by clicking "I accept" below (this "Release").<br/><br/>

  I AM FULLY INFORMED, AWARE AND UNDERSTAND THAT GETTING A TATTOO IS A POTENTIALLY DANGEROUS ACTIVITY AND INVOLVES INHERENT RISKS. I ACKNOWLEDGE THAT ANY INJURIES THAT I SUSTAIN MAY RESULT FROM OR BE COMPOUNDED BY THE ACTIONS, OMISSIONS, OR NEGLIGENCE OF THE COMPANY. <strong>NOTWITHSTANDING THE RISK, I ACKNOWLEDGE THAT I AM KNOWINGLY AND VOLUNTARILY PARTICIPATING IN THE ACTIVITY WITH AN EXPRESS UNDERSTANDING OF THE DANGER INVOLVED AND HEREBY AGREE TO ACCEPT AND ASSUME ANY AND ALL RISKS ARISING FROM GETTING A TATTOO FROM THE COMPANY, WHETHER CAUSED BY THE NEGLIGENCE OF THE COMPANY OR OTHERWISE. SPECIFICALLY, I REPRESENT, WARRANT AND AGREE (AS APPLICABLE) THAT:</strong><br/><br/>

  &emsp;&emsp;2. Risks, known and unknown, of obtaining a tattoo can lead to injury including but not limited to infection, scarring, difficulties in the detection of skin cancers or conditions, and allergic or other adverse reactions to tattoo pigment, materials or products used in application, aftercare products and/or soap. Having been informed of the potential risks associated with getting a tattoo, I wish to proceed with the tattoo procedure and application and freely accept and expressly assume any and all risks that may arise from tattooing.<br/><br/>
  &emsp;&emsp;3. The Company has given me the full opportunity to ask any question about the procedure and application of my tattoo and all of my questions have been answered to my satisfaction and I have been given instructions on aftercare. The procedure and application may result in infection, particularly if I fail to follow the aftercare instructions given to me.<br/><br/>
  &emsp;&emsp;4. I am not under the influence of alcohol or drugs, and I am voluntarily submitting to be tattooed by the Company without duress or coercion. I do not have diabetes, epilepsy, hemophilia, heart condition(s), any communicable disease and do not take any medication that works as or has side effects of blood thinning. I do not have any other medical or skin condition that may interfere with the procedure, application or healing of the tattoo. I am not the recipient of an organ or bone marrow transplant or, if I am, I have taken the prescribed preventative regimen of antibiotics that is required by my doctor in advance of any invasive procedure such as tattooing or piercing. I am not pregnant or nursing. I do not have a mental impairment that may affect my judgement in getting the tattoo.<br/><br/>
  &emsp;&emsp;5. I have provided or selected my tattoo design myself. Accordingly, the Company is not responsible for the meaning or spelling of the symbol or text that I have provide to them or chosen from design examples. I acknowledge that variations in color and design may exist between the tattoo art I have selected and the actual tattoo when it is applied. I also understand that over time, the colors and the clarity of my tattoo will fade due to unprotected exposure to the sun and the naturally occurring dispersion of pigment under the skin.<br/><br/>
  &emsp;&emsp;6. A tattoo is a permanent change to my appearance and can only be removed by laser or surgical means, which can be disfiguring and/or costly and which in all likelihood will not result in the restoration of my skin to its exact appearance before being tattooed.<br/><br/>
  &emsp;&emsp;7. I acknowledge that the Company has a NO REFUND policy on tattoos, piercing and/or retail sales, and I will not ask for a refund for any reason whatsoever.<br/><br/>

  I hereby expressly waive and release any and all claims which I may have, or which I may hereafter have, whether known or unknown, against the Company, and its officers, directors, managers, employees, agents, contractors, affiliates, successors, and assigns (collectively, "Releasees"), arising out of or attributable to the Activity, whether arising out of the ordinary negligence of the Company or any Releasees or otherwise, including those claims that may be unknown to me or which I do not suspect at this time. I covenant not to make or bring any such claim against the Company or any other Releasee, and forever release and discharge the Company and all other Releasees from liability under such claims. This waiver and release does not extend to claims for gross negligence, intentional or reckless misconduct, or any other liabilities that applicable law does not permit to be released by agreement.<br/><br/>

  I hereby consent to receive from any licensed hospital, physician, or medical personnel any medical treatment deemed necessary if I am injured or require medical attention the procedure or application of the tattoo. I understand and agree that I am solely responsible for all costs related to such medical treatment and any related medical transportation and/or evacuation and I release all parties from any type of liability for anything that may happen during my treatment.<br/><br/>

  This Release constitutes the sole and entire agreement of the Company and me with respect to the subject matter contained herein and supersedes all prior and contemporaneous understandings, agreements, representations, and warranties, both written and oral, with respect to such subject matter. If any term or provision of this Release is held invalid, illegal, or unenforceable in any jurisdiction, such invalidity, illegality, or unenforceability shall not affect any other term or provision of this Release or invalidate or render unenforceable such term or provision in any other jurisdiction. This Release is binding on and shall inure to the benefit of the Company and me and their respective heirs, successors, and assigns. All matters arising out of or relating to this Release shall be governed by and construed in accordance with the internal laws of the state in which the Company is located (as set forth above) without giving effect to any choice or conflict of law provision or rule. Any claim or cause of action arising under this Release may be brought only in the federal and state courts located in or having jurisdiction over such location and I hereby consent to the exclusive jurisdiction of such courts.<br/><br/>

  <strong>BY CLICKING "I ACCEPT" BELOW, I ACKNOWLEDGE THAT I HAVE READ AND FULLY UNDERSTOOD ALL OF THE TERMS OF THIS RELEASE AND THAT I AM VOLUNTARILY GIVING UP SUBSTANTIAL LEGAL RIGHTS, INCLUDING THE RIGHT TO SUE THE COMPANY FOR CLAIMS, WHETHER KNOWN OR UNKNOWN, ARISING OUT OF THE ACTIVITY.</strong>
  `;
}

export function hasAvailabilityChanged(
  oldAvailability: OnSchedCommonType.Availability,
  newAvailability: OnSchedCommonType.Availability,
) {
  const oldKeys = Object.keys(oldAvailability) as ShortDay[];
  const newKeys = Object.keys(newAvailability) as ShortDay[];
  if (oldKeys.length !== newKeys.length) return true;
  return oldKeys.some(key => {
    const oldDay = oldAvailability[key];
    const newDay = newAvailability[key];
    return (
      oldDay.startTime !== newDay.startTime || oldDay.endTime !== newDay.endTime
    );
  });
}

export function orderAvailabilityListByDay(
  availabilityList: [string, OnSchedCommonType.Time][],
) {
  return availabilityList.sort(
    (a, b) => DAYS_ORDER.indexOf(a[0]) - DAYS_ORDER.indexOf(b[0]),
  );
}

export function orderAvalabilityDays(days: string[]) {
  return days.sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b));
}

export function getPayoutDistribution(
  employee: EmployeeToBusiness | null | undefined,
) {
  const isAllToShop =
    employee?.payrollType === 'WAGE' ||
    (employee?.payrollType === 'OTHER' &&
      employee.payeeForOtherPayroll === 'BUSINESS');

  const isAllToArtist =
    employee?.payrollType === 'RENT' ||
    (employee?.payrollType === 'OTHER' &&
      employee.payeeForOtherPayroll === 'EMPLOYEE');

  const payoutDistribution = isAllToShop
    ? PayoutDistributionOptions.BUSINESS
    : isAllToArtist
    ? PayoutDistributionOptions.EMPLOYEE
    : PayoutDistributionOptions.COMMISSION;

  return payoutDistribution;
}

export function getPayoutText(employee: EmployeeToBusiness | null | undefined) {
  const payoutDistribution = getPayoutDistribution(employee);
  const payoutText =
    payoutDistribution === PayoutDistributionOptions.COMMISSION
      ? 'Commission Split'
      : payoutDistribution === PayoutDistributionOptions.BUSINESS
      ? 'Pay to Shop'
      : 'Pay to Artist';
  return payoutText;
}

export const getAllBlockedDaysFromTimeBlock = (timeBlock: ResourceBlock) => {
  const blockedDays = new Array<string>();
  const { startDate, endDate, repeats, repeat } = timeBlock;

  // Parse the start and end dates
  const startWithoutTime = startDate.split('T')[0];
  const endWithoutTime = endDate.split('T')[0];
  const start = `${startWithoutTime}T00:00:00`;
  const end = `${endWithoutTime}T00:00:00`;

  let startDateObj = new Date(start);
  const endDateObj = endOfDay(new Date(end));

  if (!repeats) {
    // If it does not repeat, just block the start date
    blockedDays.push(start);
  } else if (repeat && repeat.frequency === 'D') {
    // If it repeats daily, block all the days between start and end based on interval
    const interval = repeat.interval || 1; // Default to 1 if not specified
    while (
      isBefore(startDateObj, endDateObj) ||
      isSameDay(startDateObj, endDateObj)
    ) {
      blockedDays.push(format(startDateObj, 'yyyy-MM-dd'));
      startDateObj = addDays(startDateObj, interval); // Add interval days instead of 1
    }
  } else if (repeat && repeat.frequency === 'W' && repeat.weekdays) {
    const weekdays = repeat.weekdays.split('').map(d => parseInt(d, 10));
    const interval = repeat.interval || 1; // Default to 1 if not specified
    let weekCounter = 0;

    while (
      isBefore(startDateObj, endDateObj) ||
      isSameDay(startDateObj, endDateObj)
    ) {
      const startDay = startDateObj.getDay();
      // Only include days in weeks that match the interval
      if (weekdays.includes(startDay) && weekCounter % interval === 0) {
        blockedDays.push(format(startDateObj, 'yyyy-MM-dd'));
      }

      startDateObj = addDays(startDateObj, 1);
      // Increment week counter when we move to a new week (on Sunday)
      if (startDateObj.getDay() === 0) {
        weekCounter++;
      }
    }
  }

  return blockedDays;
};

export const getAllBlockedDaysFromTimeBlocks = (timeBlocks: ResourceBlock[]) =>
  timeBlocks.reduce(
    (acc, timeBlock) => [...acc, ...getAllBlockedDaysFromTimeBlock(timeBlock)],
    [] as string[],
  );

export function getAllDaysFromTrip(trip: Trips) {
  const startDateInTripTimezone = formatInTimeZone(
    trip.startDateTime,
    trip.timezone,
    'yyyy-MM-dd',
  );
  const endDateInTripTimezone = formatInTimeZone(
    trip.endDateTime,
    trip.timezone,
    'yyyy-MM-dd',
  );
  const startDate = new Date(`${startDateInTripTimezone}T00:00:00`);
  const endDate = new Date(`${endDateInTripTimezone}T00:00:00`);
  const daysBetween = Math.abs(differenceInDays(startDate, endDate));
  return Array.from({ length: daysBetween + 1 }, (_, i) =>
    format(addDays(startDate, i), 'yyyy-MM-dd'),
  );
}

export function getAllDaysFromTrips(trips: Trips[]) {
  return trips.reduce(
    (acc, trip) => [...acc, ...getAllDaysFromTrip(trip)],
    [] as string[],
  );
}

/**
 * Applythe time block's hours to a given date and convert it from the employee's timezone to the local timezone
 */
export function getTimeBlockTimes({
  timeBlock,
  date: passedDate,
  employeeTimezone: timezone,
  employeeTimezoneOffset,
}: {
  timeBlock: ResourceBlock;
  employeeTimezone?: string;
  date?: string;
  employeeTimezoneOffset?: number;
}) {
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const employeeTimezone = timezone || localTimezone;
  const date = passedDate || format(new Date(), 'yyyy-MM-dd');

  const dateWithTimeRemoved = date.split('T')[0];
  const dateStringWithTime = `${dateWithTimeRemoved}T00:00:00`;
  const startDateTime = new Date(dateStringWithTime);
  const endDateTime = new Date(dateStringWithTime);

  const endTime = timeBlock.endTime === 2400 ? 2359 : timeBlock.endTime;
  const startHours =
    typeof employeeTimezoneOffset === 'number'
      ? convertOnschedHoursToDateFromOffset(
          timeBlock.startTime,
          employeeTimezoneOffset,
        )
      : convertOnschedHoursToDateFromTimezone(
          timeBlock.startTime,
          employeeTimezone,
        );
  const endHours =
    typeof employeeTimezoneOffset === 'number'
      ? convertOnschedHoursToDateFromOffset(endTime, employeeTimezoneOffset)
      : convertOnschedHoursToDateFromTimezone(endTime, employeeTimezone);
  startDateTime.setHours(startHours.getHours(), startHours.getMinutes());
  endDateTime.setHours(endHours.getHours(), endHours.getMinutes());

  return {
    start: startDateTime,
    end: endDateTime,
  };
}

export function getDayNamesFromTimeBlock(
  timeBlock: ResourceBlock,
  plural?: boolean,
) {
  if (!timeBlock.repeats || !timeBlock.repeat) return [];
  const { repeat } = timeBlock;
  if (repeat.frequency === 'D') {
    return (DAYS_ORDER as ShortDay[]).map(day =>
      convertShortDayToFull(day, plural),
    );
  }
  return (
    repeat.weekdays
      ?.split('')
      .map(d => parseInt(d, 10))
      .sort()
      .map(day => convertShortDayToFull(convertIndexToShortDay(day), plural)) ??
    []
  );
}

export function getRoleFromSelectedRoles(
  selectedRoles: EmployeesToBusinesses['selectedRoles'],
): Exclude<Roles, 'CUSTOMER'> {
  const isManager = MANAGER_ROLES.some(role => selectedRoles.includes(role));
  return isManager ? 'SHOP_MANAGER' : 'EMPLOYEE';
}

export function getSelectedRolesFromEmployee(
  employeeRole: Roles,
  employee:
    | Pick<
        EmployeesToBusinesses,
        | 'employeeId'
        | 'selectedRoles'
        | 'canViewReports'
        | 'canManageTeam'
        | 'canManageWaivers'
        | 'isBookable'
      >
    | null
    | undefined,
  services?: BusinessService[],
): EmployeesRole[] {
  if (!isUserEmployeeOrAdmin(employeeRole)) return [];
  if (employee?.selectedRoles?.length) {
    return employee.selectedRoles as EmployeesRole[];
  }
  const roles: EmployeesRole[] = [];
  const hasManagePermissions =
    employee?.canManageTeam &&
    employee?.canManageWaivers &&
    employee?.canViewReports;
  const isShopManager =
    isUserAdminOrManager(employeeRole) && hasManagePermissions;
  const isShopBooker =
    isUserAdminOrManager(employeeRole) && !hasManagePermissions;
  const isArtist = employee?.isBookable;
  if (isShopManager) roles.push(EmployeesRole.SHOP_MANAGER);
  if (isShopBooker) roles.push(EmployeesRole.SHOP_BOOKER);
  if (isArtist) roles.push(EmployeesRole.ARTIST);

  const isServiceBookable =
    employee &&
    services?.some(service =>
      service.employeesToServices.some(
        e => e.employeeId === employee.employeeId,
      ),
    );

  if (isServiceBookable) roles.push(EmployeesRole.PIERCER);
  return roles;
}

type RolesPermissions = Pick<
  CreateArtistValues,
  | 'canManageTeam'
  | 'canViewReports'
  | 'canManageWaivers'
  | 'canManageCampaigns'
  | 'isBookable'
>;

export function getPermissionsFromSelectedRoles(
  previousSelectedRoles: EmployeesToBusinesses['selectedRoles'],
  selectedRoles: EmployeesToBusinesses['selectedRoles'],
  values: CreateArtistValues,
  currentUser: LoadedUser | null,
): RolesPermissions {
  const isBookable = selectedRoles.includes(EmployeesRole.ARTIST);
  const permissions: RolesPermissions = {
    canManageTeam: values.canManageTeam,
    canManageWaivers: values.canManageWaivers,
    canViewReports: values.canViewReports,
    canManageCampaigns: values.canManageCampaigns,
    isBookable,
  };

  const newRoles = selectedRoles.filter(
    role => !previousSelectedRoles.includes(role),
  );
  const removedRoles = previousSelectedRoles.filter(
    role => !selectedRoles.includes(role),
  );
  const updatedRoles = [...newRoles, ...removedRoles];
  const checkedRoles = [EmployeesRole.SHOP_BOOKER, EmployeesRole.SHOP_MANAGER];

  // return current values
  if (!checkedRoles.some(role => updatedRoles.includes(role))) {
    return permissions;
  }

  const isShopManager =
    getRoleFromSelectedRoles(selectedRoles) === 'SHOP_MANAGER';

  const hasManagePermissions = selectedRoles.includes(
    EmployeesRole.SHOP_MANAGER,
  );

  permissions.canManageTeam =
    isShopManager && hasManagePermissions && !!currentUser?.canManageTeam;
  permissions.canManageWaivers =
    isShopManager && hasManagePermissions && !!currentUser?.canManageWaivers;
  permissions.canViewReports =
    isShopManager && hasManagePermissions && !!currentUser?.canViewReports;
  permissions.canManageCampaigns =
    isShopManager && hasManagePermissions && !!currentUser?.canManageCampaigns;

  return permissions;
}

export const getInitialSelectedArtists = (
  loadedUser: LoadedUser,
  artistOptions: FilterOption[],
  name: string,
): string[] => {
  if (loadedUser!.role === 'EMPLOYEE') {
    return [loadedUser.userId];
  }

  const cachedArtists = sessionStorage.getItem(name);
  const parsedCachedArtists = cachedArtists ? JSON.parse(cachedArtists) : [];
  if (Array.isArray(parsedCachedArtists) && parsedCachedArtists.length > 0) {
    const filteredCachedList = parsedCachedArtists.filter(artist =>
      artistOptions.some(option => option.value === artist),
    );
    if (filteredCachedList.length > 0) return filteredCachedList;
  }

  return artistOptions.map(option => option.value);
};

export const getInitialArtistOptions = (
  loadedUser: LoadedUser,
  {
    includeFirstAvailable,
    includeUnassigned,
  }: { includeFirstAvailable?: boolean; includeUnassigned?: boolean } = {},
): FilterOption[] => {
  if (loadedUser!.role === 'EMPLOYEE') {
    return [
      {
        label: getArtistName(loadedUser!.employee),
        value: loadedUser!.userId,
        color: generateColorForArtist(loadedUser!.userId, loadedUser!),
      },
    ];
  }
  const options = getArtistOptions(loadedUser!, includeFirstAvailable);
  if (includeUnassigned && isUserAdminOrManager(loadedUser)) {
    options.push({
      label: 'Unassigned',
      value: UNASSIGNED_RESOURCE_ID,
      color: UNASSIGNED_RESOURCE_COLOR,
    });
  }
  return options;
};

export const getTimeBlockData = ({
  employee,
  employeesResourceMap,
  timeBlock,
}: {
  employee: EmployeeToBusiness | null | undefined;
  employeesResourceMap: EmployeesResourceMap | undefined;
  timeBlock: ResourceBlock | null | undefined;
}) => {
  const employeeTimezoneOffset = employee
    ? getEmployeeTimezoneOffset(employee.employeeId, employeesResourceMap)
    : undefined;

  const startDateString = timeBlock
    ? format(new Date(`${timeBlock.startDate}T00:00:00`), 'MMMM d, yyyy')
    : null;
  const endDateString = timeBlock
    ? format(new Date(`${timeBlock.endDate}T00:00:00`), 'MMMM d, yyyy')
    : null;

  const isAllDay =
    !!timeBlock && timeBlock.startTime === 0 && timeBlock.endTime === 2400;

  const { start, end } = timeBlock
    ? getTimeBlockTimes({
        timeBlock,
        employeeTimezoneOffset,
        employeeTimezone: employee?.user?.timezone,
      })
    : { start: null, end: null };

  return {
    name: timeBlock?.reason,
    employeeTimezoneOffset,
    startDateString,
    endDateString,
    isAllDay,
    start,
    end,
  };
};

// Syncs isVirtual/virtualMeetingUrl/virtualMeetingProvider with an artist's
// consultation preference; phone providers count as virtual without a URL
export const setVirtualFieldsFromEmployee = (
  setFieldValue: (field: string, value: unknown) => void,
  employee:
    | Pick<
        EmployeeToBusiness,
        'defaultVirtualProvider' | 'consultationPermanentVirtualLink'
      >
    | undefined,
  isVirtual: boolean,
) => {
  const isPhone = employee?.defaultVirtualProvider === 'PHONE';
  const hasVirtualUrl = !!employee?.consultationPermanentVirtualLink;
  const shouldBeVirtual = isVirtual && (hasVirtualUrl || isPhone);

  setFieldValue('isVirtual', shouldBeVirtual);
  setFieldValue(
    'virtualMeetingUrl',
    shouldBeVirtual && !isPhone
      ? employee?.consultationPermanentVirtualLink || ''
      : '',
  );
  setFieldValue(
    'virtualMeetingProvider',
    shouldBeVirtual ? employee?.defaultVirtualProvider ?? null : null,
  );
};

// Defaults pricing to the artist's hourly rate unless any price field
// was already touched by the user
export const applyArtistHourlyRateDefault = (
  hourlyRate: number | null | undefined,
  touched: {
    priceType?: unknown;
    price?: unknown;
    minPrice?: unknown;
    maxPrice?: unknown;
    hourlyPrice?: unknown;
  },
  setFieldValue: (field: string, value: unknown) => void,
) => {
  if (
    !hourlyRate ||
    touched.priceType ||
    touched.price ||
    touched.minPrice ||
    touched.maxPrice ||
    touched.hourlyPrice
  ) {
    return;
  }
  setFieldValue('priceType', 'HOURLY');
  setFieldValue('hourlyPrice', hourlyRate);
};

export const parseLocalYMD = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d); // local midnight
};

export type TeamMemberStatus =
  | 'OFFBOARDING'
  | 'OFFBOARDING_SCHEDULED'
  | 'INVITED'
  | 'ACTIVE';

export const getTeamMemberStatus = (
  employee: EmployeeToBusiness,
  offboarding?: Offboarding | null,
): TeamMemberStatus => {
  const scheduledForMs = offboarding?.scheduledFor
    ? new Date(offboarding.scheduledFor).getTime()
    : null;
  if (
    offboarding &&
    !offboarding.completedAt &&
    !offboarding.canceledAt &&
    scheduledForMs
  ) {
    return scheduledForMs <= Date.now()
      ? 'OFFBOARDING'
      : 'OFFBOARDING_SCHEDULED';
  }
  return employee.user?.authServiceId ? 'ACTIVE' : 'INVITED';
};
